import { useEffect, useState } from 'react';
import { DT, DS2 } from '../theme/designTokens';
import { useIsMobile } from '../hooks/useBreakpoint';
import { PageContent } from '../components/ui/PageContent';
import { Footer } from '../components/shell/Footer';
import { AtmosphericGlow } from '../components/ui/AtmosphericGlow';
import { apiClient } from '../network/ApiClient';
import { useFriends } from '../context/FriendContext';
import type { Achievement } from '../types/achievements';
import { useTranslation } from '../i18n/react';
import type { TFunction } from 'i18next';
import { formatDate } from '../i18n/format';
import { localizedAchievementName, localizedAchievementDescription } from '../i18n/achievements';

const CATEGORY_ORDER = ['milestone', 'wins', 'streaks', 'gameplay', 'social', 'customization'];

/** Known backend categories → localized labels; unknown categories fall back
    to the raw backend string (never parsed, never guessed). */
function categoryLabel(t: TFunction<'achievements'>, category: string): string {
  switch (category) {
    case 'milestone':     return t('category.milestone');
    case 'wins':          return t('category.wins');
    case 'streaks':       return t('category.streaks');
    case 'gameplay':      return t('category.gameplay');
    case 'social':        return t('category.social');
    case 'customization': return t('category.customization');
    default:              return category;
  }
}

function Card({ children, isMobile }: { children: React.ReactNode; isMobile: boolean }) {
  return (
    <section
      style={{
        background:   '#131313',
        border:       `1px solid ${DS2.color.gold}`,
        borderRadius: DS2.radius.card,
        padding:      isMobile ? '24px 20px' : '30px 44px 38px',
        marginTop:    isMobile ? 24 : 40,
      }}
    >
      {children}
    </section>
  );
}

function CardHeading({ title }: { title: string }) {
  return (
    <h2 style={{ fontFamily: DS2.font.body, fontWeight: 600, fontSize: 'clamp(1.2rem, 1.4vw, 24px)', color: DS2.color.textBright, margin: 0 }}>
      {title}
    </h2>
  );
}

function TrophyBadgeSVG({ unlocked, size = 34 }: { unlocked: boolean; size?: number }) {
  const color = unlocked ? '#C9A84C' : '#5A5A5A';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M7 4h10v4a5 5 0 0 1-10 0V4Z" stroke={color} strokeWidth="1.75" strokeLinejoin="round" />
      <path d="M7 5H4v1.5A3.5 3.5 0 0 0 7.5 10" stroke={color} strokeWidth="1.75" strokeLinecap="round" />
      <path d="M17 5h3v1.5A3.5 3.5 0 0 1 16.5 10" stroke={color} strokeWidth="1.75" strokeLinecap="round" />
      <path d="M12 13v3M9 20h6M9.5 20c0-2 .8-3 2.5-3s2.5 1 2.5 3" stroke={color} strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function AchievementTile({ achievement, isMobile }: { achievement: Achievement; isMobile: boolean }) {
  const { t } = useTranslation('achievements');
  const { unlocked } = achievement;
  return (
    <div
      style={{
        display:      'flex',
        gap:          14,
        alignItems:   'flex-start',
        padding:      isMobile ? '14px' : '16px 18px',
        borderRadius: 14,
        border:       `1px solid ${unlocked ? 'rgba(201,168,76,0.6)' : 'rgba(255,255,255,0.08)'}`,
        background:   unlocked ? 'rgba(201,168,76,0.08)' : 'rgba(255,255,255,0.02)',
        opacity:      unlocked ? 1 : 0.7,
      }}
    >
      <span style={{ flexShrink: 0, marginTop: 2 }}>
        <TrophyBadgeSVG unlocked={unlocked} />
      </span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontFamily: DS2.font.heading, fontWeight: 700, fontSize: isMobile ? 14 : 15.5, color: unlocked ? '#F5F0E0' : '#B8B8B8' }}>
          {localizedAchievementName(achievement)}
        </div>
        <div style={{ fontFamily: DS2.font.body, fontWeight: 300, fontSize: isMobile ? 12.5 : 13.8, lineHeight: 1.4, color: '#A89F86', marginTop: 3 }}>
          {localizedAchievementDescription(achievement)}
        </div>
        {unlocked && achievement.unlocked_at && (
          <div style={{ fontFamily: DS2.font.body, fontSize: 11.5, color: DT.color.success, marginTop: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {t('unlockedOn', { date: formatDate(achievement.unlocked_at, { month: 'short', day: 'numeric', year: 'numeric' }) })}
          </div>
        )}
      </div>
    </div>
  );
}

export function AchievementsDS2Screen() {
  const { t } = useTranslation('achievements');
  const isMobile = useIsMobile();
  const { lastUnlockedAchievement } = useFriends();
  const [achievements, setAchievements] = useState<Achievement[] | null>(null);
  // Error state holds a flag, not prose — the message resolves through t() at
  // render so it relabels on a runtime language switch (Phase 2 convention).
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    apiClient.getAchievements()
      .then(res => {
        if (!alive) return;
        setAchievements(res.achievements);
      })
      .catch(() => { if (alive) setError(true); });
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!lastUnlockedAchievement) return;
    setAchievements(prev => {
      if (!prev) return prev;
      const idx = prev.findIndex(a => a.key === lastUnlockedAchievement.key);
      if (idx === -1 || prev[idx].unlocked) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], unlocked: true, unlocked_at: new Date().toISOString() };
      return next;
    });
  }, [lastUnlockedAchievement]);

  const unlockedCount = (achievements ?? []).filter(a => a.unlocked).length;
  const totalCount = achievements?.length ?? 0;

  const grouped = new Map<string, Achievement[]>();
  (achievements ?? []).forEach(a => {
    const list = grouped.get(a.category) ?? [];
    list.push(a);
    grouped.set(a.category, list);
  });
  const categories = CATEGORY_ORDER.filter(c => grouped.has(c));

  return (
    <div style={{ width: '100%', background: DT.color.pageBg, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ position: 'relative', paddingTop: isMobile ? 28 : 64, paddingBottom: isMobile ? 40 : 88 }}>
        {!isMobile && (
          <div aria-hidden="true" style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <AtmosphericGlow width={1300} height={760} top={130} leftOffset={-250} />
            <AtmosphericGlow width={1300} height={760} top={770} leftOffset={320} />
          </div>
        )}

        <div style={{ position: 'relative' }}>
          <PageContent maxWidth={DS2.layout.contentMaxWidth}>
            <div style={{ maxWidth: 830 }}>
              <div>
                <h1
                  style={{
                    fontFamily:    DS2.font.heading,
                    fontWeight:    700,
                    fontSize:      isMobile ? 32 : `clamp(2rem, ${DS2.type.sectionHeading.pref}vw, ${DS2.type.sectionHeading.max}px)`,
                    lineHeight:    isMobile ? '38px' : DS2.type.sectionHeading.lineHeight,
                    letterSpacing: '-0.02em',
                    color:         '#F5F0E0',
                    margin:        0,
                  }}
                >
                  {t('title')}
                </h1>
                <p
                  style={{
                    fontFamily: DS2.font.body,
                    fontWeight: 300,
                    fontSize:   isMobile ? 15 : `clamp(1rem, ${DS2.type.paragraph.pref}vw, ${DS2.type.paragraph.max}px)`,
                    lineHeight: 1.6,
                    color:      DS2.color.textBright,
                    margin:     '12px 0 0',
                    maxWidth:   520,
                  }}
                >
                  {totalCount > 0 ? t('subtitle', { unlocked: unlockedCount, total: totalCount }) : t('subtitleEmpty')}
                </p>
              </div>

              {error && (
                <div role="alert" style={{ marginTop: 24, fontFamily: DS2.font.body, fontSize: 14, color: DT.color.error }}>
                  {t('error')}
                </div>
              )}

              {!achievements && !error && (
                <div style={{ marginTop: 40, fontFamily: DS2.font.body, fontSize: 14, color: '#CCCCCC' }}>{t('loading')}</div>
              )}

              {categories.map(category => (
                <Card key={category} isMobile={isMobile}>
                  <CardHeading title={categoryLabel(t, category)} />
                  <div style={{ marginTop: 18, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)', gap: 14 }}>
                    {(grouped.get(category) ?? []).map(a => (
                      <AchievementTile key={a.key} achievement={a} isMobile={isMobile} />
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </PageContent>
        </div>
      </div>

      <Footer />
    </div>
  );
}
